import React, { useCallback, useContext, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import MinimizeIcon from '@mui/icons-material/Minimize';
import { AppContext, SnackContext } from 'src/store/ContextStore';
import { ModalDateFieldProvider } from 'src/components/UI/input/ModalDateFieldContext';
import { CONSTANTS as Q_CONSTANTS } from 'src/components/questionnaire/Constants';
import { PEDIGREE } from 'src/components/Constants';
import { useBulkEditState } from './useBulkEditState';
import MemberTable from './MemberTable';
import {
  saveDiseases,
  deleteDisease,
  savePhenotypes,
  deletePhenotype,
  saveGenotypes,
  deleteGenotype
} from './bulkEditApi';
import BulkClinicalSection from './BulkClinicalSection';

export default function BulkEditModal({
  open,
  onClose,
  memberRecords,
  setMemberRecords,
  setPedigreeState,
  setLabelsSet,
  familyId,
  personLockHook
}) {
  const { content } = useContext(AppContext);
  const { setSnack } = useContext(SnackContext);
  const { pedigreeSelectedUsers: users } = content;
  const user = users?.selectedUser;

  const {
    draft,
    getEntry,
    isDirty,
    dirtyCount,
    updatePersonField,
    updatePersonFields,
    addDisease,
    removeNewDisease,
    markDiseaseRemoved,
    unmarkDiseaseRemoved,
    addPhenotype,
    removeNewPhenotype,
    markPhenotypeRemoved,
    addGenotype,
    removeNewGenotype,
    markGenotypeRemoved,
    reset
  } = useBulkEditState();

  const [saving, setSaving] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [uiMode, setUiMode] = useState('normal'); // 'normal' | 'maximised' | 'minimised'
  const [batchLockConflicts, setBatchLockConflicts] = useState([]);

  // Batch-acquire locks for all members when modal opens; release on close
  React.useEffect(() => {
    if (!open || !personLockHook) return;
    const personIds = memberRecords.map((m) => m.pid);
    // No familyId argument: the hook is already bound to one and scopes the
    // request itself (TGCLOUD-1767). The second argument was never read.
    personLockHook.acquireBatch(personIds).then((result) => {
      if (!result.success) {
        setBatchLockConflicts(result.conflicts || []);
        onClose();
      }
    });
    return () => {
      personLockHook.releaseBatch(personIds);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = dirtyCount(memberRecords);

  const actions = {
    updatePersonField,
    updatePersonFields,
    addDisease,
    removeNewDisease,
    markDiseaseRemoved,
    unmarkDiseaseRemoved,
    addPhenotype,
    removeNewPhenotype,
    markPhenotypeRemoved,
    addGenotype,
    removeNewGenotype,
    markGenotypeRemoved
  };

  const handleCancel = () => {
    if (dirty > 0) {
      setCancelConfirmOpen(true);
    } else {
      reset();
      onClose();
    }
  };

  const handleCancelConfirm = () => {
    setCancelConfirmOpen(false);
    reset();
    onClose();
  };

  const handleSaveAll = useCallback(async () => {
    if (saving) return;
    setSaving(true);

    const errors = [];
    let updatedRecords = [...memberRecords];

    // Collect new disease labels for labelsSet update
    const newDiseaseLabels = {};

    for (const member of memberRecords) {
      const entry = draft.get(member.pid);
      if (!entry) continue;

      // ── Person field changes ────────────────────────────────────────────
      const personChanged =
        entry.genderIdentity !== null ||
        entry.isAlive !== null ||
        entry.dob !== null ||
        entry.dod !== null ||
        entry.firstName !== null ||
        entry.lastName !== null ||
        entry.ethnicity !== null ||
        entry.mobile !== null ||
        entry.email !== null ||
        entry.hospitalNumber !== null ||
        entry.healthcareId !== null ||
        entry.isHide !== null ||
        entry.recordsReviewed !== null;

      if (personChanged) {
        updatedRecords = updatedRecords.map((m) => {
          if (m.pid !== member.pid) return m;
          const updates = {};
          if (entry.genderIdentity !== null) {
            updates.genderIdentity = entry.genderIdentity;
            updates.gender =
              Q_CONSTANTS.genderMappings[entry.genderIdentity] ?? m.gender;
          }
          if (entry.isAlive !== null) updates.isAlive = entry.isAlive;
          if (entry.dob !== null) updates.dob = entry.dob;
          if (entry.dod !== null) updates.dod = entry.dod;
          if (entry.firstName !== null) updates.firstName = entry.firstName;
          if (entry.lastName !== null) updates.lastName = entry.lastName;
          if (entry.ethnicity !== null) updates.ethnicity = entry.ethnicity;
          if (entry.mobile !== null) updates.mobile = entry.mobile;
          if (entry.email !== null) updates.email = entry.email;
          if (entry.hospitalNumber !== null)
            updates.hospitalNumber = entry.hospitalNumber;
          if (entry.healthcareId !== null)
            updates.healthcareId = entry.healthcareId;
          if (entry.isHide !== null) updates.isHide = entry.isHide;
          if (entry.recordsReviewed !== null)
            updates.recordsReviewed = entry.recordsReviewed;
          return { ...m, ...updates };
        });
      }

      // ── Disease deletes ────────────────────────────────────────────────
      for (const mongoId of entry.removedDiseaseIds) {
        try {
          await deleteDisease(user, member, mongoId);
          updatedRecords = updatedRecords.map((m) =>
            m.pid === member.pid
              ? {
                  ...m,
                  diagnoses: (m.diagnoses || []).filter(
                    (d) => d._id !== mongoId
                  )
                }
              : m
          );
        } catch (e) {
          errors.push(
            `Failed to delete disease for ${
              member.upn ? `T${member.upn}` : member.pid
            }`
          );
        }
      }

      // ── Disease additions ─────────────────────────────────────────────
      if (entry.newDiseases.length > 0) {
        try {
          const res = await saveDiseases(user, member, entry.newDiseases);
          if (res?.data?.allDiagnoses) {
            updatedRecords = updatedRecords.map((m) =>
              m.pid === member.pid
                ? { ...m, diagnoses: res.data.allDiagnoses }
                : m
            );
            entry.newDiseases.forEach((d) => {
              if (!(d.id in newDiseaseLabels)) newDiseaseLabels[d.id] = true;
            });
          }
        } catch (e) {
          errors.push(
            `Failed to save diseases for ${
              member.upn ? `T${member.upn}` : member.pid
            }: ${e.message}`
          );
        }
      }

      // ── Phenotype deletes ──────────────────────────────────────────────
      for (const phenotypeId of entry.removedPhenotypeIds) {
        try {
          await deletePhenotype(user, member, phenotypeId);
          updatedRecords = updatedRecords.map((m) =>
            m.pid === member.pid
              ? {
                  ...m,
                  phenotypes: (m.phenotypes || []).filter(
                    (p) => p.id !== phenotypeId
                  )
                }
              : m
          );
        } catch (e) {
          errors.push(
            `Failed to delete phenotype for ${
              member.upn ? `T${member.upn}` : member.pid
            }`
          );
        }
      }

      // ── Phenotype additions ───────────────────────────────────────────
      if (entry.newPhenotypes.length > 0) {
        try {
          const res = await savePhenotypes(user, member, entry.newPhenotypes);
          if (res?.data?.allPhenotypes) {
            updatedRecords = updatedRecords.map((m) =>
              m.pid === member.pid
                ? { ...m, phenotypes: res.data.allPhenotypes }
                : m
            );
          }
        } catch (e) {
          errors.push(
            `Failed to save phenotypes for ${
              member.upn ? `T${member.upn}` : member.pid
            }: ${e.message}`
          );
        }
      }

      // ── Genotype deletes ───────────────────────────────────────────────
      for (const genotypeId of entry.removedGenotypeIds) {
        try {
          await deleteGenotype(user, member, genotypeId);
          updatedRecords = updatedRecords.map((m) =>
            m.pid === member.pid
              ? {
                  ...m,
                  genotypes: (m.genotypes || []).filter(
                    (g) => g.id !== genotypeId
                  )
                }
              : m
          );
        } catch (e) {
          errors.push(
            `Failed to delete genotype for ${
              member.upn ? `T${member.upn}` : member.pid
            }`
          );
        }
      }

      // ── Genotype additions ─────────────────────────────────────────────
      if (entry.newGenotypes.length > 0) {
        try {
          const res = await saveGenotypes(user, member, entry.newGenotypes);
          if (res?.data?.allGenotypes) {
            updatedRecords = updatedRecords.map((m) =>
              m.pid === member.pid
                ? { ...m, genotypes: res.data.allGenotypes }
                : m
            );
          }
        } catch (e) {
          errors.push(
            `Failed to save genotypes for ${
              member.upn ? `T${member.upn}` : member.pid
            }: ${e.message}`
          );
        }
      }
    }

    // Flush person-field changes to pedigree (triggers pedigree auto-save)
    const hasPersonChanges = [...draft.values()].some(
      (e) =>
        e.genderIdentity !== null ||
        e.isAlive !== null ||
        e.dob !== null ||
        e.dod !== null ||
        e.firstName !== null ||
        e.lastName !== null ||
        e.ethnicity !== null ||
        e.mobile !== null ||
        e.email !== null ||
        e.hospitalNumber !== null ||
        e.healthcareId !== null ||
        e.isHide !== null ||
        e.recordsReviewed !== null
    );
    if (hasPersonChanges) {
      setPedigreeState((prev) => ({
        ...prev,
        save: PEDIGREE.saveStatus.IN_PROGRESS
      }));
    }

    // Update labelsSet for newly added diseases
    if (Object.keys(newDiseaseLabels).length > 0 && setLabelsSet) {
      setLabelsSet((prev) => ({
        ...prev,
        diseasesMap: {
          ...prev.diseasesMap,
          ...Object.fromEntries(
            Object.entries(newDiseaseLabels).filter(
              ([id]) => !(id in prev.diseasesMap)
            )
          )
        }
      }));
    }

    setMemberRecords(updatedRecords);

    setSaving(false);
    reset();

    // WHAT THIS MAY AND MAY NOT CLAIM. The diagnosis and genotype writes above
    // are awaited against their own endpoints, so by here they have genuinely
    // landed. The PERSON fields have not: they were handed to `setMemberRecords`,
    // and the only thing that writes those to the server is PedigreeChart's
    // debounced autosave, which has not run yet and may be refused outright (a
    // foreign family lock, the 409 foreign-member gate, a 413). Claiming "all
    // changes saved" over a write that has not been attempted is the same defect
    // as PersonMenu's optimistic tick.
    //
    // So the message reports PROGRESS whenever person fields are in play, and
    // the single completion message — the "Saved just now" chip, or the failure
    // snackbar — comes from the autosave's settle handler, which is the only
    // code that knows the outcome. There is deliberately no second success
    // snack here.
    if (errors.length > 0) {
      setSnack({
        status: true,
        msg: `Finished with ${errors.length} error(s): ${errors[0]}`,
        severity: 'warning'
      });
    } else if (hasPersonChanges) {
      setSnack({
        status: true,
        msg: 'Saving changes…',
        severity: 'info'
      });
      onClose();
    } else {
      // Nothing went through the pedigree autosave: every write on this path
      // was awaited above, so completion is the truth.
      setSnack({ status: true, msg: 'All changes saved', severity: 'success' });
      onClose();
    }
  }, [draft, memberRecords, user, saving]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="lg"
        fullWidth
        fullScreen={uiMode === 'maximised'}
        slotProps={{
          paper: {
            sx:
              uiMode === 'minimised'
                ? { display: 'none' }
                : uiMode === 'maximised'
                ? { display: 'flex', flexDirection: 'column' }
                : { height: '85vh', display: 'flex', flexDirection: 'column' }
          }
        }}
      >
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700
                }}
              >
                Bulk edit family members
              </Typography>
              {familyId && (
                <Chip
                  label={`${familyId} · ${memberRecords.length} members`}
                  size="small"
                  color="primary"
                />
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <Tooltip title="Minimise">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setUiMode('minimised')}
                    disabled={saving}
                    sx={{ color: 'white' }}
                  >
                    <MinimizeIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={uiMode === 'maximised' ? 'Restore' : 'Maximise'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setUiMode((m) =>
                        m === 'maximised' ? 'normal' : 'maximised'
                      )
                    }
                    disabled={saving}
                    sx={{ color: 'white' }}
                  >
                    {uiMode === 'maximised' ? (
                      <FullscreenExitIcon fontSize="small" />
                    ) : (
                      <FullscreenIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton
                size="small"
                onClick={handleCancel}
                disabled={saving}
                sx={{ color: 'white' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <ModalDateFieldProvider>
          {/* ── Table ────────────────────────────────────────────────────── */}
          <DialogContent
            sx={{
              p: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <MemberTable
              memberRecords={memberRecords}
              draft={draft}
              isDirty={isDirty}
              actions={actions}
              familyId={familyId}
            />
          </DialogContent>

          {/* ── Bulk clinical record creation ────────────────────────────── */}
          <BulkClinicalSection
            memberRecords={memberRecords}
            familyId={familyId}
          />
        </ModalDateFieldProvider>

        {/* ── Footer status bar ───────────────────────────────────────────── */}
        <Box
          sx={{
            px: 3,
            py: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary'
            }}
          >
            {dirty > 0
              ? `${dirty} row${dirty !== 1 ? 's' : ''} with unsaved changes`
              : 'No unsaved changes'}
          </Typography>
          <Divider orientation="vertical" flexItem />
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary'
            }}
          >
            Expand a row to add diseases, phenotypes, or genotypes
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveAll}
              disabled={saving || dirty === 0}
              endIcon={
                saving ? <CircularProgress size={14} color="inherit" /> : null
              }
            >
              Save all changes
            </Button>
          </Box>
        </Box>
      </Dialog>
      {/* ── Minimised taskbar ───────────────────────────────────────────── */}
      {uiMode === 'minimised' && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1400,
            bgcolor: 'primary.main',
            color: 'white',
            px: 2,
            py: 1,
            borderRadius: '8px 8px 0 0',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            boxShadow: 3
          }}
        >
          <Typography variant="body2">
            {familyId} — Bulk Edit in progress
          </Typography>
          {dirty > 0 && (
            <Chip
              label={`${dirty} unsaved`}
              size="small"
              color="warning"
              sx={{ fontWeight: 'bold' }}
            />
          )}
          <Button
            size="small"
            color="inherit"
            variant="outlined"
            sx={{ borderColor: 'rgba(255,255,255,0.5)', ml: 1 }}
            onClick={() => setUiMode('normal')}
          >
            Restore
          </Button>
          <IconButton
            size="small"
            sx={{ color: 'white', ml: 0.5 }}
            onClick={handleCancel}
            disabled={saving}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      {/* ── Batch lock conflict dialog ───────────────────────────────────── */}
      {batchLockConflicts.length > 0 && (
        <Dialog
          open={batchLockConflicts.length > 0}
          onClose={() => setBatchLockConflicts([])}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>Bulk Edit Unavailable</DialogTitle>
          <DialogContent sx={{ pt: '16px !important' }}>
            <Typography
              variant="body2"
              gutterBottom
              sx={{
                color: 'text.primary'
              }}
            >
              The following member
              {batchLockConflicts.length !== 1 ? 's are' : ' is'} currently
              being edited by another user. Please try again once they have
              finished.
            </Typography>
            <List dense disablePadding sx={{ mt: 1 }}>
              {batchLockConflicts.map((c) => {
                const memberIdx = memberRecords.findIndex(
                  (m) => String(m.pid) === String(c.personId)
                );
                const member = memberIdx >= 0 ? memberRecords[memberIdx] : null;
                const upn = member?.upn ?? c.personId;
                const nodeNumber = memberIdx >= 0 ? memberIdx + 1 : null;
                const fullName = [member?.firstName, member?.lastName]
                  .filter(Boolean)
                  .join(' ');
                // Format: "G-8-00009 · UPN nodeNumber - First Last"
                const label = [
                  familyId,
                  `· UPN ${upn}`,
                  fullName ? `- ${fullName}` : null
                ]
                  .filter((p) => p != null)
                  .join(' ');
                return (
                  <ListItem key={c.personId} disableGutters sx={{ py: 0.25 }}>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600
                          }}
                        >
                          {label}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary'
                          }}
                        >
                          Currently edited by {c.lockedByName}
                        </Typography>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 2 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setBatchLockConflicts([])}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {/* ── Cancel confirmation dialog ───────────────────────────────────── */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        role="alertdialog"
        aria-label="Confirm discard"
      >
        <DialogTitle sx={{ pb: 1 }}>Discard unsaved changes?</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary'
            }}
          >
            You have{' '}
            <strong>
              {dirty} row{dirty !== 1 ? 's' : ''} with unsaved changes
            </strong>
            . Cancelling will discard all of them.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            autoFocus
            onClick={() => setCancelConfirmOpen(false)}
          >
            Keep editing
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={handleCancelConfirm}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
